<script lang="ts">
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import type { CloudRecording } from '../types/cloud-recordings';
	import type { Device } from 'src/types/Device';
	import { onDestroy, onMount } from 'svelte';
	import { getCloudRecordings } from 'src/services/cam-cloud/recordings';
	import { Container, Text, Loader, Stack } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import RecordingsListItem from './RecordingsTab/RecordingsListItem.svelte';
	import NoResults from './RecordingsTab/NoResults.svelte';
	import RecordingSetupCta from './RecordingsTab/RecordingSetupCta.svelte';
	import RecordingsLoadError from './RecordingsTab/RecordingsLoadError.svelte';
	import { onCameraRecordingChange } from 'src/components/Camera/helpers/camera-thumbnail-watch';
	import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
	import { needsEncryptionSetup } from 'src/components/Camera/helpers/recording-key-management';

	export let device: Device;
	export let deviceId: string;

	const RECORDINGS_LIMIT = 3;

	let recordings: CloudRecording[] = [];
	let isLoading = true;
	let loadError = false;
	let showSetupCta = false;

	const refreshSetupState = async (currentDevice: Device) => {
		showSetupCta = await needsEncryptionSetup(currentDevice);
	};

	// Re-check when the local key backup changes (e.g. right after provisioning).
	$: {
		$camera_storage_keys;
		refreshSetupState(device);
	}

	const loadRecordings = async () => {
		// Recordings belong to the owner's account, so a guest has none to list.
		if (device?.shared) {
			isLoading = false;
			return;
		}
		loadError = false;
		try {
			const result = await getCloudRecordings(deviceId, { limit: RECORDINGS_LIMIT, offset: 0 });
			recordings = result.items as CloudRecording[];
		} catch (error) {
			console.log('Failed to load recordings', error);
			loadError = true;
		} finally {
			isLoading = false;
		}
	};

	const handleRetry = () => {
		loadError = false;
		loadRecordings();
	};

	const navigateToSetting = (setting, subSetting, params) => {
		routeToDeviceSettings.setTarget(deviceId, setting, subSetting, params);
	};

	const handleSelect = (event) => {
		const recording = event.detail;
		navigateToSetting('camera_recordings', 'view', recording);
	};

	onMount(loadRecordings);

	const unsubscribe = onCameraRecordingChange(deviceId, () => loadRecordings());
	onDestroy(unsubscribe);
</script>

<Text styleType="body" class="text-bold mt-m"
	>{$format('last_recordings_title', { default: 'Last Recordings' })}</Text
>
{#if device?.shared}
	<Container class="pb-l">
		<NoResults
			noResultsFound={{
				icon: 'fa-light fa-lock',
				title: $format('recordings_list_shared_device', { default: 'Shared device' }),
				description: $format('recordings_list_shared_device_description', {
					default: 'This camera is shared with you, so its recordings stay with its owner.',
				}),
			}}
		/>
	</Container>
{:else if isLoading}
	<div class="d-flex justify-content-center width-full p-m">
		<Loader />
	</div>
{:else if loadError}
	<Container class="pb-l">
		<RecordingsLoadError on:retry={handleRetry} />
	</Container>
{:else if showSetupCta && recordings.length === 0}
	<Container class="pb-l">
		<RecordingSetupCta {deviceId} variant="empty" />
	</Container>
{:else if recordings.length > 0}
	{#if showSetupCta}
		<RecordingSetupCta {deviceId} variant="banner" class="mt-m" />
	{/if}
	<Stack gap="s">
		{#each recordings as recording}
			<RecordingsListItem {recording} selected={false} variant="simple" on:select={handleSelect} />
		{/each}
	</Stack>
{:else}
	<Container class="pb-l">
		<NoResults />
	</Container>
{/if}
